// ─── Category dedupe (cross-month UUID churn) ──────────────────
//
// Every monthly budget regenerates its budget_categories rows with FRESH ids.
// Any query that loads categories across months (e.g. `.in('user_id', userIds)`
// with no budget filter, or user + partner budgets) gets N copies of each
// category — N months of history = N "Housing" rows. Matching by exact UUID
// across months is also wrong: a transaction's category_id usually points to a
// different month's row than the one on screen.
//
// THE RULE: identify a category by its canonical name `parent::child`
// (lowercased, trimmed), never by UUID, whenever rows can span months.
//
// This bug class has bitten us three times (Reports.js aggregation, the Log
// Edit sheet selection, the Receipt Scanner picker). Any new category picker
// or category lookup should be checked against this pattern.
//
// Pickers using this module (dedupeCategories + resolveCategoryId):
//   - ReceiptScanner      — category picker + merchant-history auto-select
//   - QuickAddTransaction — "All categories" picker, recent/suggested pills
//   - Log EditTransactionSheet — category picker + current-category highlight
// Other places that follow the same canonical-name rule inline:
//   - Reports.js          — canonOf() when aggregating month spending
//   - BulkImport.js       — per-row <select> dedupes by parent::child
//   - Log filter + Recategorize sheets — flat lists, dedupe by name only
//     (dedupeCategoryNames; the filter itself matches by name)

const norm = (s) => (s || '').toLowerCase().trim()

// Canonical "parent::child" key for a row, given a by-id lookup of all rows.
export function canonicalCategoryKey(cat, byId) {
  if (!cat) return null
  const parent = cat.parent_id ? byId[cat.parent_id] : null
  return `${parent ? norm(parent.name) : ''}::${norm(cat.name)}`
}

// Rank a budget for representative selection: current month first, then past
// months most-recent-first, then future months nearest-first.
function budgetRank(budget, now) {
  if (!budget) return -Infinity
  const cur = now.getFullYear() * 12 + now.getMonth()
  const b = budget.year * 12 + (budget.month - 1)
  if (b === cur) return Infinity
  if (b < cur) return b                // past: larger = more recent
  return -1e6 - (b - cur)              // future: below every past month
}

// Collapse rows spanning months/partners into one row per canonical name.
//   rows    — budget_categories rows (need id, name, parent_id, budget_id)
//   budgets — [{ id, month, year }] for the rows' budget_ids (optional)
//   userId  — tie-break: prefer the current user's row over the partner's
// Returns representative rows (shallow copies) in sort_order, with each
// child's parent_id re-pointed at its parent's representative so the
// parent → subcategory grouping in pickers stays intact.
export function dedupeCategories(rows, { budgets = [], userId = null, now = new Date() } = {}) {
  if (!rows || rows.length === 0) return []
  const byId = {}
  rows.forEach(r => { byId[r.id] = r })
  const budgetById = {}
  budgets.forEach(b => { budgetById[b.id] = b })

  const score = (r) => [budgetRank(budgetById[r.budget_id], now), r.user_id === userId ? 1 : 0]
  const better = (a, b) => {
    const [ra, ua] = score(a), [rb, ub] = score(b)
    return ra !== rb ? ra > rb : ua > ub
  }

  const repByKey = new Map()
  rows.forEach(r => {
    if (!norm(r.name)) return
    const key = canonicalCategoryKey(r, byId)
    const cur = repByKey.get(key)
    if (!cur || better(r, cur)) repByKey.set(key, r)
  })

  const reps = Array.from(repByKey.values())
  const repIdByKey = {}
  repByKey.forEach((r, k) => { repIdByKey[k] = r.id })

  return reps
    .map(r => {
      if (!r.parent_id) return { ...r }
      const parent = byId[r.parent_id]
      const parentRepId = parent ? repIdByKey[canonicalCategoryKey(parent, byId)] : null
      return { ...r, parent_id: parentRepId || r.parent_id }
    })
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
}

// Map any month's category id to the deduped picker's equivalent id (same
// canonical name). Returns '' when the id can't be resolved.
//   allRows — the full, un-deduped rows (so old-month ids can be looked up)
export function resolveCategoryId(id, allRows, deduped) {
  if (!id) return ''
  if (deduped.some(c => c.id === id)) return id
  const byId = {}
  allRows.forEach(r => { byId[r.id] = r })
  const key = canonicalCategoryKey(byId[id], byId)
  if (!key) return ''
  const dedupedById = {}
  deduped.forEach(r => { dedupedById[r.id] = r })
  const match = deduped.find(c => canonicalCategoryKey(c, dedupedById) === key)
  return match ? match.id : ''
}

// Load every household category row plus the budgets they belong to.
export async function fetchHouseholdCategories(supabase, userIds) {
  const [catsRes, budgetsRes] = await Promise.all([
    supabase.from('budget_categories').select('*').in('user_id', userIds).order('sort_order'),
    supabase.from('budgets').select('id, month, year').in('user_id', userIds),
  ])
  return { rows: catsRes.data || [], budgets: budgetsRes.data || [] }
}
